// ABOUTME: Generic enter/exit reveal presets — turns an open/closed boolean into a CSS style object.
// ABOUTME: UI-agnostic: any overlay that already gates itself on a boolean can swap fade for a tween.

/**
 * One place to author how a piece of chrome arrives and leaves.
 *
 * The reel's overlays all animate the same way — mount at the closed state, flip a boolean a beat
 * later, unmount one duration after it flips back (`useFadePresence`, and PointTagLayer's dwell
 * machine). What differed per surface was only the CSS: everything hardcoded
 * `opacity` + `transition: opacity 400ms ease` inline, so changing the treatment on ONE surface
 * meant editing that surface's component and keeping its timing constant in step with the
 * lifecycle timer that drives it.
 *
 * This module owns the CSS half of that contract and nothing else. It is deliberately pure — no
 * hooks, no refs, no DOM — so it composes with whatever already decides `open`: the dwell machine,
 * `useFadePresence`, a plain `useState`, or a visual-regression harness rendering a single frame.
 *
 * Adding a treatment is adding an entry to `PRESETS`. Callers name it by string, so a component
 * takes one `reveal` prop and never learns what the motion actually is.
 */

/** The house duration. Matches the 400ms every lifecycle timer in the reel is already built on. */
export const REVEAL_MS = 400;

/** Names, so callers import a constant rather than spelling a string that can go stale. */
export const Reveal = {
  FADE: 'fade',
  SCALE_CORNER: 'scaleCorner',
  WIPE_CORNER: 'wipeCorner',
};

export const DEFAULT_REVEAL = Reveal.FADE;

// Ease-out-back-free "expo out": fast off the mark, long settle. A tween that decelerates is what
// reads as the panel *arriving*; a symmetric ease reads as a slide.
const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
// Closing is quicker than opening — chrome that lingers on the way out feels sluggish, and the
// dwell machine unmounts on the full duration either way, so a faster leg just finishes early.
const EASE_IN = 'cubic-bezier(0.55, 0, 1, 0.45)';

/**
 * Scale a tween's closed state from zero rather than from nothing: a box that starts at exactly 0
 * has no painted pixels for the compositor to interpolate a backdrop-filter against on some
 * engines, and the first frame flashes. 0.01 is visually identical and always paints.
 */
const MIN_CLOSED_SCALE = 0.01;

/** `transform` values compose left-to-right; the caller's own transform must stay outermost. */
const composeTransform = (base, extra) => [base, extra].filter(Boolean).join(' ') || undefined;

const PRESETS = {
  /** The original treatment. Opacity only — nothing moves, nothing repaints outside the box. */
  [Reveal.FADE]: ({ open, durationMs, baseTransform }) => ({
    opacity: open ? 1 : 0,
    transform: composeTransform(baseTransform, null),
    transition: `opacity ${durationMs}ms ease`,
    willChange: 'opacity',
  }),

  /**
   * The desktop tag treatment (client, 2026-09-03): the plate grows out of its own top-left corner
   * and collapses back into it. `transformOrigin` is what makes it a corner tween rather than a
   * centre pop — with an absolutely-positioned box that origin is the anchor point itself, so the
   * tag appears to unfold from exactly the spot it labels instead of ballooning around it.
   *
   * Opacity rides along on a SHORTER leg than the scale. At small scales the plate is a dense
   * little smudge of blurred backdrop; fading it in over the first ~60% of the tween hides that
   * and leaves the tail of the motion to play out at full opacity, which is where the ease-out
   * actually reads.
   */
  [Reveal.SCALE_CORNER]: ({ open, durationMs, origin, baseTransform, closedScale }) => ({
    opacity: open ? 1 : 0,
    transformOrigin: origin,
    transform: composeTransform(baseTransform, `scale(${open ? 1 : closedScale})`),
    transition: `transform ${durationMs}ms ${open ? EASE_OUT : EASE_IN}, `
      + `opacity ${Math.round(durationMs * 0.6)}ms ${open ? 'ease-out' : 'ease-in'}`,
    willChange: 'transform, opacity',
  }),

  /**
   * The same corner gesture without scaling the type: the box is clipped back to its top-left
   * corner and the clip opens diagonally. Kept as a first-class preset because a surface with
   * small copy (a caption, a spec row) distorts under `scale` in a way a plate of large numerals
   * does not — this is the treatment to reach for there.
   */
  [Reveal.WIPE_CORNER]: ({ open, durationMs, baseTransform }) => ({
    opacity: 1,
    clipPath: open ? 'inset(0 0 0 0)' : 'inset(0 100% 100% 0)',
    transform: composeTransform(baseTransform, null),
    transition: `clip-path ${durationMs}ms ${open ? EASE_OUT : EASE_IN}`,
    willChange: 'clip-path',
  }),
};

/** Every registered preset name — for tests, and for a caller validating authored config. */
export const revealPresetNames = () => Object.keys(PRESETS);

/**
 * Build the inline style for one reveal state.
 *
 * @param {{
 *   preset?: string,          // a `Reveal.*` name; an unknown name falls back to fade, loudly
 *   open: boolean,            // the caller's gate — true = shown, false = entering/leaving
 *   durationMs?: number,      // MUST match whatever timer unmounts the element
 *   origin?: string,          // transform-origin for the corner presets
 *   baseTransform?: string,   // the caller's own transform (e.g. a compositor-layer hint)
 *   closedScale?: number,     // scale at the closed end of a scale tween
 *   reducedMotion?: boolean,  // downgrade any motion preset to a plain fade
 * }} options
 * @returns {object} a style object to spread onto the element
 */
export function revealStyle({
  preset = DEFAULT_REVEAL,
  open,
  durationMs = REVEAL_MS,
  origin = 'top left',
  baseTransform = null,
  closedScale = MIN_CLOSED_SCALE,
  reducedMotion = false,
} = {}) {
  // A guest who has asked the OS for less motion gets the opacity treatment on every surface, not
  // a slower tween — the whole point of the preference is that nothing travels across the frame.
  const name = reducedMotion ? DEFAULT_REVEAL : preset;
  const build = PRESETS[name] || PRESETS[DEFAULT_REVEAL];
  return build({
    open: Boolean(open), durationMs, origin, baseTransform, closedScale,
  });
}

export default revealStyle;
